import type Phaser from "phaser";
import type { PairData, Move } from "../types";

/**
 * Кетбой — спрайт с подменой текстуры под текущую позу.
 * Никаких готовых Phaser-анимаций (animations.create) не используем —
 * мы вручную меняем texture key и/или дёргаем тиннинг для синхронной игры
 * хёрта обеих фигур пары.
 */
export default class Catboy {
  public sprite: Phaser.GameObjects.Sprite;
  private idleTimer?: Phaser.Time.TimerEvent;
  private idleFlip = false;

  constructor(
    private scene: Phaser.Scene,
    x: number,
    y: number,
    public readonly pair: PairData,
    private readonly facing: "right" | "left",
  ) {
    this.sprite = scene.add.sprite(x, y, pair.catboyKeys.idle1).setOrigin(0.5, 1.0);
    if (facing === "left") this.sprite.setFlipX(true);
    this.startIdle();
  }

  setDepth(d: number): this {
    this.sprite.setDepth(d);
    return this;
  }

  setPosition(x: number, y: number): this {
    this.sprite.setPosition(x, y);
    return this;
  }

  /** Idle-цикл переключает 2 кадра по 500 мс. */
  startIdle(): void {
    this.stopIdle();
    this.sprite.setTexture(this.pair.catboyKeys.idle1);
    this.idleFlip = false;
    this.idleTimer = this.scene.time.addEvent({
      delay: 500,
      loop: true,
      callback: () => {
        this.idleFlip = !this.idleFlip;
        this.sprite.setTexture(this.idleFlip ? this.pair.catboyKeys.idle2 : this.pair.catboyKeys.idle1);
      },
    });
  }

  stopIdle(): void {
    this.idleTimer?.remove();
    this.idleTimer = undefined;
  }

  /**
   * Проиграть позу выполнения команды. Возвращает Promise, который резолвится
   * после полного цикла анимации позы (~600 мс).
   */
  playPose(move: Move): Promise<void> {
    this.stopIdle();
    const k = this.pair.catboyKeys;
    return new Promise((resolve) => {
      switch (move.catboyPose) {
        case "kneel":
          this.sprite.setTexture(k.kneel);
          this.scene.tweens.add({
            targets: this.sprite,
            y: this.sprite.y + 4,
            duration: 200,
            yoyo: true,
            onComplete: () => resolve(),
          });
          break;
        case "crawl": {
          const startX = this.sprite.x;
          const dir = this.facing === "right" ? 1 : -1;
          let frame = 0;
          this.sprite.setTexture(k.crawl1);
          const t = this.scene.time.addEvent({
            delay: 120,
            repeat: 5,
            callback: () => {
              frame++;
              this.sprite.setTexture(frame % 2 === 0 ? k.crawl1 : k.crawl2);
            },
          });
          this.scene.tweens.add({
            targets: this.sprite,
            x: startX + 14 * dir,
            duration: 720,
            yoyo: false,
            onComplete: () => {
              t.remove();
              this.scene.tweens.add({
                targets: this.sprite,
                x: startX,
                duration: 200,
                onComplete: () => resolve(),
              });
            },
          });
          break;
        }
        case "purr":
          this.sprite.setTexture(k.purr);
          this.scene.tweens.add({
            targets: this.sprite,
            scaleX: 1.05,
            scaleY: 1.05,
            duration: 150,
            yoyo: true,
            repeat: 1,
            onComplete: () => {
              this.sprite.setScale(1, 1);
              resolve();
            },
          });
          break;
        case "beg":
          this.sprite.setTexture(k.beg);
          this.scene.tweens.add({
            targets: this.sprite,
            angle: -3,
            duration: 150,
            yoyo: true,
            repeat: 1,
            onComplete: () => {
              this.sprite.setAngle(0);
              resolve();
            },
          });
          break;
      }
    });
  }

  /** Отыграть смущение (получение урона) — короткий blush-цикл. */
  playBlush(): Promise<void> {
    this.stopIdle();
    return new Promise((resolve) => {
      const k = this.pair.catboyKeys;
      let frame = 0;
      this.sprite.setTexture(k.blush1);
      const t = this.scene.time.addEvent({
        delay: 120,
        repeat: 3,
        callback: () => {
          frame++;
          this.sprite.setTexture(frame % 2 === 0 ? k.blush1 : k.blush2);
        },
      });
      this.scene.time.delayedCall(560, () => {
        t.remove();
        resolve();
      });
    });
  }

  /** Поза тревоги за свою монстродевку при поражении. */
  playWorry(): void {
    this.stopIdle();
    this.sprite.setTexture(this.pair.catboyKeys.worry);
  }

  /** Цикл бега для escape-анимации. Двигается заданием извне. */
  startRun(): void {
    this.stopIdle();
    const k = this.pair.catboyKeys;
    let frame = 0;
    this.sprite.setTexture(k.run1);
    this.idleTimer = this.scene.time.addEvent({
      delay: 120,
      loop: true,
      callback: () => {
        frame++;
        this.sprite.setTexture(frame % 2 === 0 ? k.run1 : k.run2);
      },
    });
  }
}
